import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faSlidersH,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useEffect, useRef, useState } from 'react';

import { AudioDevicePanel } from '@/components/AudioDeviceSelector/AudioDeviceSelector';
import { NumberPad } from '@/components/NumberPad/NumberPad';
import AppContext from '@/contexts/appContext';
import { isSoftPhoneActive, useSoftPhone } from '@/contexts/softPhoneContext';
import useActiveCallCloseWarning from '@/hooks/useActiveCallCloseWarning';
import { SoftphoneAddParticipantPayload } from '@/types/softphone';

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const ActiveCallOverlay = () => {
  const { config, addMessage } = useContext(AppContext);
  const softPhone = useSoftPhone();
  const activeSoftPhone = isSoftPhoneActive(softPhone) ? softPhone : null;
  const activeCall = activeSoftPhone?.activeCall ?? null;
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [connected, setConnected] = useState(false);
  const [inConference, setInConference] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [addCallOpen, setAddCallOpen] = useState(false);
  const [addCallLoading, setAddCallLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useActiveCallCloseWarning(Boolean(activeCall));

  useEffect(() => {
    if (!activeCall) {
      setElapsed(0);
      setConnected(false);
      setMuted(false);
      setKeypadOpen(false);
      setAddCallOpen(false);
      setInConference(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    activeCall.on('accept', () => {
      setConnected(true);
      intervalRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [activeCall]);

  if (!activeCall) return null;

  const toNumber = activeCall.parameters?.To ?? '';

  const handleMute = () => {
    const next = !muted;
    activeCall.mute(next);
    setMuted(next);
  };

  const handleHangUp = () => {
    activeSoftPhone?.endCall();
  };

  const handleAudioToggle = () => {
    if (!audioOpen && activeSoftPhone?.enumerateDevices) {
      setAudioLoading(true);
      activeSoftPhone?.enumerateDevices().finally(() => setAudioLoading(false));
    }
    setAudioOpen((prev) => !prev);
  };

  const handleKeypadToggle = () => {
    setAddCallOpen(false);
    setKeypadOpen((prev) => !prev);
  };

  const handleAddCallToggle = () => {
    setKeypadOpen(false);
    setAddCallOpen((prev) => !prev);
  };

  const bridgeParticipant = async (participant: SoftphoneAddParticipantPayload) => {
    if (!activeSoftPhone?.addParticipant) return;
    setAddCallLoading(true);
    try {
      await activeSoftPhone.addParticipant(participant);
      setInConference(true);
      setAddCallOpen(false);
    } catch {
      addMessage('error', 'Could not add that participant to the call. Please try again.');
    } finally {
      setAddCallLoading(false);
    }
  };

  const handleAddCallSubmit = (number: string) => {
    void bridgeParticipant({ to_number: number });
  };

  // The real number is privileged, so the server resolves the preset for us.
  const handleCrisisLine = () => {
    void bridgeParticipant({ preset: 'crisis_line' });
  };

  const statusLabel = inConference ? 'Conference' : connected ? 'Connected' : 'Calling…';

  return (
    <div className="fixed bottom-4 inset-x-4 sm:left-1/2 sm:right-auto sm:inset-x-auto sm:-translate-x-1/2 z-50 flex flex-col items-center gap-2">
      {audioOpen && (
        <AudioDevicePanel
          onClose={() => setAudioOpen(false)}
          loading={audioLoading}
          className="mb-1"
        />
      )}
      {keypadOpen && (
        <NumberPad
          mode="dtmf"
          activeCall={activeCall}
          onClose={() => setKeypadOpen(false)}
          title="Keypad"
          className="mb-1"
        />
      )}
      {addCallOpen && (
        <NumberPad
          mode="dialpad"
          onDial={handleAddCallSubmit}
          onClose={handleAddCallToggle}
          callDisabled={addCallLoading}
          title="Add a Participant"
          className="mb-1"
        />
      )}
      <div className="w-full sm:w-auto bg-uniform-gray-900 text-uniform-white-base rounded-xl sm:rounded-full px-5 py-3 shadow-lg flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        {/* Status row — takes full width on mobile, inline on sm+ */}
        <div className="flex items-center gap-4 sm:contents">
          <div className="flex flex-col leading-tight flex-1 sm:flex-none">
            <span className="text-xs text-uniform-gray-300">{statusLabel}</span>
            <span className="text-sm font-medium">{toNumber}</span>
          </div>
          {connected && (
            <span className="text-xs text-uniform-gray-300 tabular-nums w-10 text-center">
              {formatDuration(elapsed)}
            </span>
          )}
        </div>
        {/* Action buttons — grid on mobile, inline row on sm+ */}
        <div className="flex flex-wrap justify-center gap-2 sm:contents">
          <button
            type="button"
            onClick={handleMute}
            title={muted ? 'Unmute' : 'Mute'}
            aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-uniform-gray-700 hover:bg-uniform-gray-600 transition-colors">
            <FontAwesomeIcon icon={muted ? faMicrophoneSlash : faMicrophone} className="text-sm" />
          </button>
          <button
            type="button"
            onClick={handleAudioToggle}
            title="Audio settings"
            aria-label="Audio settings"
            aria-pressed={audioOpen}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-uniform-gray-700 hover:bg-uniform-gray-600 transition-colors">
            <FontAwesomeIcon icon={faSlidersH} className="text-sm" />
          </button>
          <button
            type="button"
            onClick={handleKeypadToggle}
            title={keypadOpen ? 'Close keypad' : 'Open keypad'}
            aria-label={keypadOpen ? 'Close keypad' : 'Open keypad'}
            aria-pressed={keypadOpen}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${keypadOpen ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-uniform-gray-700 hover:bg-uniform-gray-600'}`}>
            <svg
              xmlnsXlink="http://www.w3.org/1999/xlink"
              viewBox="0 0 48 48"
              className="svg-inline--fa fa-sliders text-sm"
              xmlns="http://www.w3.org/2000/svg">
              <g id="Layer_2" data-name="Layer 2">
                <g id="invisible_box" data-name="invisible box">
                  <rect width="48" height="48" fill="none" />
                </g>
                <g id="icons_Q2" data-name="icons Q2" fill="currentColor">
                  <g>
                    <circle cx="24" cy="24" r="5" />
                    <circle cx="9" cy="24" r="5" />
                    <circle cx="39" cy="24" r="5" />
                    <circle cx="24" cy="39" r="5" />
                    <circle cx="9" cy="39" r="5" />
                    <circle cx="39" cy="39" r="5" />
                    <circle cx="24" cy="9" r="5" />
                    <circle cx="9" cy="9" r="5" />
                    <circle cx="39" cy="9" r="5" />
                  </g>
                </g>
              </g>
            </svg>
          </button>
          {connected && (
            <button
              type="button"
              onClick={handleAddCallToggle}
              title={addCallOpen ? 'Cancel add call' : 'Add call'}
              aria-label={addCallOpen ? 'Cancel add call' : 'Add call to conference'}
              aria-pressed={addCallOpen}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${addCallOpen ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-uniform-gray-700 hover:bg-uniform-gray-600'}`}>
              <FontAwesomeIcon icon={faUserPlus} className="text-sm" />
            </button>
          )}
          {connected && config?.softphone_crisis_line_enabled && (
            <button
              type="button"
              onClick={handleCrisisLine}
              disabled={addCallLoading}
              title="Add the 988 crisis line to this call"
              aria-label="Add the 988 crisis line to this call"
              className="h-9 px-3 rounded-full flex items-center justify-center bg-amber-600 hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm font-semibold tabular-nums">
              988
            </button>
          )}
          <button
            type="button"
            onClick={handleHangUp}
            title="Hang up"
            aria-label="Hang up"
            className="w-9 h-9 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-500 transition-colors">
            <FontAwesomeIcon icon={faPhoneSlash} className="text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveCallOverlay;
